import React, { ChangeEvent, useRef, useState } from 'react';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { XmarkStroke } from '@lineiconshq/free-icons';
import { Label } from '@/components/ui/label';
import Image from '@/platform/compat/image';
import { FormMode } from '@/types/form';
import { cn } from '@/lib/utils';

interface FileSelectFieldProps {
  label?: string;
  formMode?: FormMode;
  onFileChange?: (file: File | null) => void;
  className?: string;
  labelClassName?: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const FileSelectField: React.FC<FileSelectFieldProps> = ({
  label = 'Chọn file',
  formMode = 'add',
  onFileChange,
  className = '',
  labelClassName = 'w-[100px] shrink-0 text-sm font-normal text-left flex items-center'
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    onFileChange?.(file);
  };

  const handleClick = () => {
    if (formMode !== 'view' && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    onFileChange?.(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={cn('flex items-center gap-4', className)}>
      <Label className={labelClassName}>{label}</Label>
      <div className='flex flex-1 items-center gap-2 border-b border-gray-200 py-2'>
        <Image
          src='/images/ic_file_upload.png'
          alt='Upload'
          width={20}
          height={20}
          className='cursor-pointer object-contain'
          onClick={handleClick}
        />

        {selectedFile && (
          <>
            <span className='flex-1 truncate text-sm text-blue-600'>
              {selectedFile.name} ({formatFileSize(selectedFile.size)})
            </span>
            <button
              type='button'
              onClick={handleClearFile}
              className='flex items-center justify-center text-gray-400 hover:text-gray-600'
            >
              <Lineicons aria-hidden='true' icon={XmarkStroke} className='h-4 w-4' />
            </button>
          </>
        )}

        <input
          ref={fileInputRef}
          type='file'
          accept='.xlsx,.xls'
          className='hidden'
          onChange={handleFileChange}
          disabled={formMode === 'view'}
        />
      </div>
    </div>
  );
};

export default FileSelectField;
